// Settings → Social Media tab.
//
// IMPORTANT: this is the SETTINGS tab — *OAuth account connections* only.
// The Social Media AI page (compose, schedule, analytics) is a separate
// page module.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   tab button:           line 27037
//   renderSocialPanel:    line 27108
//   OAUTH_PLATFORMS:      line 27110
//   popup OAuth flow:     line 27143-27191
//
// Backend
// -------
// kitchenos-api mounts social_oauth at `/api/social-oauth`
// (server.js:552), exposing:
//   GET  /api/social-oauth/status              — current connection state
//   GET  /api/social-oauth/connect/:platform   — initiate OAuth (redirect)
//   POST /api/social-oauth/disconnect/:platform — revoke and remove token
//
// The legacy portal opens `/connect/:platform` in a popup. Per the master
// spec we DO NOT actually wire up OAuth here — that's deferred to a later
// pass. Instead we:
//   1. GET /api/social-oauth/status to detect already-connected accounts
//      so the demo reflects reality if an admin pre-seeded a token.
//   2. "Connect" buttons stub with a toast ("Coming soon — OAuth
//      integration pending"). The legacy popup wiring is left as a
//      reference comment for whoever lights it up.
//   3. "Disconnect" calls POST /api/social-oauth/disconnect/:platform via
//      a confirm dialog. On 404/501 we fall back to a session-local edit.
//
// Platforms
// ---------
// Master spec lists "Instagram, Facebook, LinkedIn, optional TikTok". The
// kitchenos-api OAuth module supports `meta` (covers both Facebook AND
// Instagram via a single token) + `linkedin`. TikTok is rendered as a
// "Coming soon" platform since there's no backend support yet.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./social-media.css";

// ── Types ────────────────────────────────────────────────────────────────────

/** OAuth platform IDs the kitchenos-api module knows about. */
type OAuthPlatform = "meta" | "linkedin";

/** UI platform IDs — Facebook and Instagram both bind to the `meta` token. */
type PlatformId = "facebook" | "instagram" | "linkedin" | "tiktok";

interface PlatformDef {
  id: PlatformId;
  name: string;
  /** OAuth platform name (server-side). null = not yet supported. */
  oauthPlatform: OAuthPlatform | null;
  /** Background colour for the logo chip. */
  bg: string;
  /** Glyph (1–3 chars). */
  glyph: string;
  /** Subtitle / role hint. */
  hint: string;
}

interface MetaPage {
  /** Page name from the Graph API. */
  name?: string;
  /** Page username (e.g. @acme_kitchens). */
  username?: string;
  id?: string;
}

interface MetaStatus {
  connected: boolean;
  expires_at: number | null;
  pages: MetaPage[];
}

interface LinkedInOrg {
  name?: string;
  vanityName?: string;
  id?: string;
}

interface LinkedInStatus {
  connected: boolean;
  expires_at: number | null;
  organizations: LinkedInOrg[];
}

interface OAuthStatusResponse {
  ok: boolean;
  status: {
    meta?: MetaStatus;
    linkedin?: LinkedInStatus;
  };
}

interface ConnectionView {
  platformId: PlatformId;
  connected: boolean;
  /** "@handle" or "Page name" — null when nothing useful. */
  handle: string | null;
}

// ── Platform table ───────────────────────────────────────────────────────────

const PLATFORMS: readonly PlatformDef[] = [
  {
    id: "instagram",
    name: "Instagram",
    oauthPlatform: "meta",
    bg: "linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
    glyph: "IG",
    hint: "Business account",
  },
  {
    id: "facebook",
    name: "Facebook",
    oauthPlatform: "meta",
    bg: "#1877F2",
    glyph: "f",
    hint: "Page posting",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    oauthPlatform: "linkedin",
    bg: "#0A66C2",
    glyph: "in",
    hint: "Company page",
  },
  {
    id: "tiktok",
    name: "TikTok",
    oauthPlatform: null,
    bg: "#000000",
    glyph: "TT",
    hint: "Coming soon",
  },
];

const STORAGE_KEY = "tkos.set.socialDisconnects";

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function readSessionDisconnects(): Set<PlatformId> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as PlatformId[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

function writeSessionDisconnects(set: Set<PlatformId>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

/**
 * Project a server status response into per-UI-platform views. Both
 * Facebook AND Instagram derive from the single `meta` token.
 */
function buildViews(status: OAuthStatusResponse["status"]): Map<PlatformId, ConnectionView> {
  const views = new Map<PlatformId, ConnectionView>();
  const meta = status.meta;
  const linkedin = status.linkedin;

  // Instagram — first meta page that has an IG-style username if any, else
  // first page name.
  const metaConnected = !!meta?.connected;
  const firstPage = meta?.pages?.[0];
  const igHandle = firstPage?.username
    ? `@${firstPage.username}`
    : firstPage?.name ?? null;
  const fbHandle = firstPage?.name ?? (firstPage?.username ? `@${firstPage.username}` : null);

  views.set("instagram", {
    platformId: "instagram",
    connected: metaConnected,
    handle: metaConnected ? igHandle : null,
  });
  views.set("facebook", {
    platformId: "facebook",
    connected: metaConnected,
    handle: metaConnected ? fbHandle : null,
  });

  const liConnected = !!linkedin?.connected;
  const firstOrg = linkedin?.organizations?.[0];
  const liHandle = firstOrg
    ? firstOrg.name ?? (firstOrg.vanityName ? `@${firstOrg.vanityName}` : null)
    : null;
  views.set("linkedin", {
    platformId: "linkedin",
    connected: liConnected,
    handle: liConnected ? liHandle : null,
  });

  views.set("tiktok", {
    platformId: "tiktok",
    connected: false,
    handle: null,
  });

  return views;
}

// ── Render ───────────────────────────────────────────────────────────────────

function statusLine(view: ConnectionView, def: PlatformDef): string {
  if (def.oauthPlatform === null) {
    return `<span class="set-sm-status-muted">Backend support pending</span>`;
  }
  if (view.connected) {
    const handle = view.handle ? `Connected as ${escapeHtml(view.handle)}` : "Connected";
    return `<span class="set-sm-status-ok"><span class="set-sm-dot" aria-hidden="true"></span>${handle}</span>`;
  }
  return `<span class="set-sm-status-muted">Not connected</span>`;
}

function actionMarkup(view: ConnectionView, def: PlatformDef): string {
  if (def.oauthPlatform === null) {
    return `<button type="button" class="set-btn set-btn-secondary set-sm-action" disabled aria-disabled="true">Coming soon</button>`;
  }
  if (view.connected) {
    return `<button type="button" class="set-btn set-btn-danger set-sm-action" data-action="disconnect" data-id="${escapeHtml(def.id)}">Disconnect</button>`;
  }
  return `<button type="button" class="set-btn set-sm-action" data-action="connect" data-id="${escapeHtml(def.id)}">Connect</button>`;
}

function renderCard(def: PlatformDef, view: ConnectionView): string {
  return `
    <li class="set-sm-card" data-platform="${escapeHtml(def.id)}" data-connected="${view.connected}">
      <div class="set-sm-logo" style="background:${escapeHtml(def.bg)}" aria-hidden="true">${escapeHtml(def.glyph)}</div>
      <div class="set-sm-body">
        <div class="set-sm-name">${escapeHtml(def.name)}</div>
        <div class="set-sm-hint">${escapeHtml(def.hint)}</div>
        <div class="set-sm-status">${statusLine(view, def)}</div>
      </div>
      <div class="set-sm-action-cell">${actionMarkup(view, def)}</div>
    </li>
  `;
}

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  listEl: HTMLElement;
  footnoteEl: HTMLElement;
  dialogEl: HTMLDialogElement;
  dialogTitleEl: HTMLElement;
  dialogBodyEl: HTMLElement;
  dialogConfirmBtn: HTMLButtonElement;
  dialogCancelBtn: HTMLButtonElement;
} {
  container.innerHTML = `
    <section class="set-sm-root" aria-labelledby="set-sm-title">
      <header class="set-sm-head">
        <h2 class="set-section-title" id="set-sm-title">Social Media Accounts</h2>
        <p class="set-sm-help">
          Connect your accounts so the Social Media AI can post directly.
        </p>
      </header>

      <div class="set-sm-banner" data-role="banner" hidden></div>

      <ul class="set-sm-list" data-role="list" aria-label="Social media connections"></ul>

      <p class="set-sm-footnote" data-role="footnote">
        Connections are authorised via OAuth. Your credentials are stored
        securely server-side and never shared with The Kitchen OS staff.
      </p>

      <dialog class="set-sm-dialog" data-role="dialog" aria-labelledby="set-sm-dialog-title">
        <form method="dialog" class="set-sm-dialog-form">
          <h3 class="set-sm-dialog-title" id="set-sm-dialog-title" data-role="dialog-title"></h3>
          <p class="set-sm-dialog-body" data-role="dialog-body"></p>
          <div class="set-sm-dialog-actions">
            <button type="button" class="set-btn set-btn-secondary" data-role="dialog-cancel">Cancel</button>
            <button type="button" class="set-btn set-btn-danger" data-role="dialog-confirm">Disconnect</button>
          </div>
        </form>
      </dialog>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    listEl: container.querySelector<HTMLElement>("[data-role='list']")!,
    footnoteEl: container.querySelector<HTMLElement>("[data-role='footnote']")!,
    dialogEl: container.querySelector<HTMLDialogElement>("[data-role='dialog']")!,
    dialogTitleEl: container.querySelector<HTMLElement>("[data-role='dialog-title']")!,
    dialogBodyEl: container.querySelector<HTMLElement>("[data-role='dialog-body']")!,
    dialogConfirmBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-confirm']")!,
    dialogCancelBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-cancel']")!,
  };
}

function renderList(listEl: HTMLElement, views: Map<PlatformId, ConnectionView>): void {
  listEl.innerHTML = PLATFORMS.map((def) => {
    const view = views.get(def.id) ?? {
      platformId: def.id,
      connected: false,
      handle: null,
    };
    return renderCard(def, view);
  }).join("");
}

function renderBanner(el: HTMLElement, msg: string | null, kind: "info" | "error"): void {
  if (!msg) {
    el.hidden = true;
    el.textContent = "";
    el.classList.remove("set-sm-banner--error");
    return;
  }
  el.hidden = false;
  el.textContent = msg;
  el.classList.toggle("set-sm-banner--error", kind === "error");
}

// ── Mount ────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "social-media",
  label: "Social Media",
  icon: iconMarkup("chat", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ui = renderShell(container);
    let controller: AbortController | null = new AbortController();
    let disposed = false;
    let views = new Map<PlatformId, ConnectionView>();
    let isLocalMode = false;

    // Initial empty render so the cards exist before status comes back.
    views = buildViews({});
    renderList(ui.listEl, views);

    // ── Confirm dialog plumbing ────────────────────────────────────────────

    let dialogResolve: ((confirmed: boolean) => void) | null = null;

    const closeDialog = (confirmed: boolean): void => {
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(confirmed);
        dialogResolve = null;
      }
    };

    const onConfirmClick = (): void => closeDialog(true);
    const onCancelClick = (): void => closeDialog(false);
    const onDialogCancel = (e: Event): void => {
      e.preventDefault();
      closeDialog(false);
    };

    ui.dialogConfirmBtn.addEventListener("click", onConfirmClick);
    ui.dialogCancelBtn.addEventListener("click", onCancelClick);
    ui.dialogEl.addEventListener("cancel", onDialogCancel);

    const confirmDisconnect = (platform: PlatformDef): Promise<boolean> => {
      ui.dialogTitleEl.textContent = `Disconnect ${platform.name}?`;
      ui.dialogBodyEl.textContent =
        `The Social Media AI won't be able to post to ${platform.name} until you reconnect.`;
      try {
        ui.dialogEl.showModal();
      } catch {
        ui.dialogEl.close();
        ui.dialogEl.showModal();
      }
      return new Promise<boolean>((resolve) => {
        dialogResolve = resolve;
      });
    };

    // ── Network ────────────────────────────────────────────────────────────

    const load = async (): Promise<void> => {
      try {
        // social-oauth router is mounted at /api/social-oauth (server.js:552).
        const data = await ctx.api<OAuthStatusResponse>("/social-oauth/status", {
          signal: controller!.signal,
        });
        if (disposed) return;
        views = buildViews(data.status ?? {});
        // Apply any session-only disconnects so a tab-switch round-trips.
        const localDisconnects = readSessionDisconnects();
        for (const id of localDisconnects) {
          const v = views.get(id);
          if (v) views.set(id, { ...v, connected: false, handle: null });
        }
        isLocalMode = false;
        renderList(ui.listEl, views);
        renderBanner(ui.bannerEl, null, "info");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          isLocalMode = true;
          views = buildViews({});
          renderList(ui.listEl, views);
          renderBanner(
            ui.bannerEl,
            "OAuth status endpoint pending — showing all accounts as not connected.",
            "info",
          );
          return;
        }
        const msg = err instanceof Error ? err.message : "Couldn't load social connections";
        renderBanner(ui.bannerEl, msg, "error");
        ctx.showToast(msg, "error");
      }
    };

    void load();

    // ── Disconnect ─────────────────────────────────────────────────────────

    const performDisconnect = async (def: PlatformDef): Promise<void> => {
      if (def.oauthPlatform === null) return; // shouldn't happen

      const prev = views.get(def.id);
      // Optimistic — flip both Facebook AND Instagram if we're disconnecting
      // either, since they share the meta token.
      if (def.oauthPlatform === "meta") {
        const fb = views.get("facebook");
        const ig = views.get("instagram");
        if (fb) views.set("facebook", { ...fb, connected: false, handle: null });
        if (ig) views.set("instagram", { ...ig, connected: false, handle: null });
      } else {
        if (prev) views.set(def.id, { ...prev, connected: false, handle: null });
      }
      renderList(ui.listEl, views);

      // Track session-local disconnect (so a re-render after reload still
      // reflects the user's intent if the endpoint is missing).
      const sessionDisconnects = readSessionDisconnects();
      sessionDisconnects.add(def.id);
      if (def.oauthPlatform === "meta") {
        sessionDisconnects.add("facebook");
        sessionDisconnects.add("instagram");
      }

      if (isLocalMode) {
        writeSessionDisconnects(sessionDisconnects);
        ctx.showToast(`Disconnected ${def.name} (locally)`, "info");
        return;
      }

      try {
        await ctx.api<unknown>(`/social-oauth/disconnect/${def.oauthPlatform}`, {
          method: "POST",
        });
        if (disposed) return;
        ctx.showToast(`Disconnected ${def.name}`, "success");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          isLocalMode = true;
          writeSessionDisconnects(sessionDisconnects);
          ctx.showToast(`Disconnected ${def.name} (locally — endpoint pending)`, "info");
          return;
        }
        // Roll back.
        if (prev) views.set(def.id, prev);
        if (def.oauthPlatform === "meta") {
          // Roll back the sibling we also flipped.
          // Best-effort: refetch status from server.
        }
        renderList(ui.listEl, views);
        const msg = err instanceof Error ? err.message : `Couldn't disconnect ${def.name}`;
        ctx.showToast(msg, "error");
      }
    };

    // ── Click delegation ───────────────────────────────────────────────────

    const onListClick = async (e: MouseEvent): Promise<void> => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>(".set-sm-action");
      if (!btn || btn.disabled) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id as PlatformId | undefined;
      if (!action || !id) return;
      const def = PLATFORMS.find((p) => p.id === id);
      if (!def) return;

      if (action === "connect") {
        // Per spec — OAuth wiring is deferred. Stub with a friendly toast.
        // (Reference: legacy popup flow at portal-standalone.html:27143.)
        ctx.showToast("Coming soon — OAuth integration pending", "info");
        return;
      }

      if (action === "disconnect") {
        const ok = await confirmDisconnect(def);
        if (ok) await performDisconnect(def);
      }
    };

    ui.listEl.addEventListener("click", onListClick);

    // ── Cleanup ────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      controller?.abort();
      controller = null;
      ui.listEl.removeEventListener("click", onListClick);
      ui.dialogConfirmBtn.removeEventListener("click", onConfirmClick);
      ui.dialogCancelBtn.removeEventListener("click", onCancelClick);
      ui.dialogEl.removeEventListener("cancel", onDialogCancel);
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(false);
        dialogResolve = null;
      }
    };
  },
};

export default tab;
